import { useCallback, useEffect, useEffectEvent, useState } from "react";
import { useEnvironment } from "@aviv-unified-frontend/runtime/environment";

import { useOverlayOrchestrator } from "../../OverlayOrchestrator";
import { BRAND_CONFIGURATION } from "../../../../configs/brands";
import { useElapsed } from "../../../utils/useElapsed";
import { useScrolled } from "../../../utils/useScrolled";

import type { CustomBannerVariant } from "./variants/types";
import { useCustomBannerTracking } from "./useCustomBannerTracking";
import { useCustomBannerData } from "./useCustomBannerData";

const { appBanner } = BRAND_CONFIGURATION;
const KEY = "custom-banner";

type UseCustomBannerInput = {
  variant: CustomBannerVariant;
};

export const useCustomBanner = ({ variant }: UseCustomBannerInput) => {
  const {
    application: { id: applicationId },
  } = useEnvironment();

  const { dispatch } = useOverlayOrchestrator();
  const stableDispatch = useEffectEvent(dispatch);
  const { onClickEvent, onDismissEvent } = useCustomBannerTracking();
  // The initial state is set to null to avoid any flickering issues related to the use of BreakpointValue.
  const [isBannerVisible, setIsBannerVisible] = useState<boolean | null>(null);

  const { onUpdateBannerData, shouldShowBanner } = useCustomBannerData({
    variant,
  });

  const bannerInfo = applicationId ? appBanner?.custom?.[applicationId] : null;

  const isElapsed = useElapsed({
    duration: 5,
    active: true,
  });

  /**
   * Determine whether to show the banner based on the elapsed time and banner visibility flags (should show & viewports).
   */
  useEffect(() => {
    if (shouldShowBanner === false) {
      stableDispatch({ type: "DISABLE", payload: KEY });

      setIsBannerVisible(false);

      return;
    }

    setIsBannerVisible(Boolean(shouldShowBanner) && isElapsed);
  }, [isElapsed, shouldShowBanner]);

  const isScrolled = useScrolled({
    distance: 300,
    active: Boolean(isBannerVisible) && variant === "bottom",
  });

  /**
   * Hiding the banner after scrolling a certain distance.
   */
  useEffect(() => {
    if (isScrolled) {
      setIsBannerVisible(false);
      stableDispatch({ type: "COMPLETE", payload: KEY });
    }
  }, [isScrolled]);

  /**
   * Track the click event on the banner.
   */
  const onClick = useCallback(
    (link_text: string) => {
      onUpdateBannerData();
      onClickEvent(link_text);
    },
    [onUpdateBannerData, onClickEvent],
  );

  /**
   * Dismiss the banner and update local storage (banner data).
   */
  const onDismiss = useCallback(
    (shouldUpdateBannerData = true) => {
      setIsBannerVisible(false);
      shouldUpdateBannerData && onUpdateBannerData();

      onDismissEvent();
      dispatch({ type: "COMPLETE", payload: KEY });
    },
    [onUpdateBannerData, onDismissEvent, dispatch],
  );

  return {
    bannerInfo,
    isBannerVisible,
    onClick,
    onDismiss,
  };
};
